import React from 'react';
import {Box, Grid, Typography, useTheme} from '@mui/material';
import ListSubheader from '@mui/material/ListSubheader';
import {IoChevronBackCircleOutline} from 'react-icons/io5';
import LoadingButton from '@mui/lab/LoadingButton';

import CollapsibleTable from '../../../../sub-modules-menu/collapsible-table/CollapsibleTable';

import {shadowBoxSx, deepCopy} from '../../util';
import {claimLevelDataCopy, billingFinancialData} from '../data';

import {ClaimSummaryItem} from './ClaimLevelViewStyles';

const CardComponent = (props: any) => {
  const theme = useTheme();
  const data = props?.data[0] || null;
  return (
    <Box
      sx={{
        ...shadowBoxSx
      }}>
      <Typography
        sx={{
          fontSize: '1rem !important',
          fontWeight: 'bold',
          marginBottom: '1rem'
        }}>
        {props?.sectionUIData?.label}
      </Typography>
      <Grid
        sx={{
          padding: '0.2em 0.4em !important'
        }}
        container
        rowSpacing={0.5}
        columnSpacing={{xs: 1, sm: 4, md: 4}}>
        {props?.sectionUIData?.fields.map((_: any, gridItemIndex: any) => (
          <Grid item xs={12} md={12} key={gridItemIndex}>
            <ClaimSummaryItem>
              <Typography
                color="primary"
                sx={{
                  fontSize: '14px',
                  wordWrap: 'break-word',
                  width: '40%',
                  color: `${theme.palette.common.black}`
                }}>
                {`${_.label} :`}
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  width: '65%',
                  fontWeight: 'bold'
                }}>
                {data ? data[_.data_column] : '-'}
              </Typography>
            </ClaimSummaryItem>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const ClaimLevelView = (props: any) => {
  const theme = useTheme();
  const [gridData, setGridData] = React.useState([]);
  const [patientData, setPatientData] = React.useState<any>([]);
  const [subscriberData, setSubscriberData] = React.useState<any>([]);
  const [payerData, setPayerData] = React.useState<any>([]);
  const [facilityProviderData, setFacilityProviderData] = React.useState<any>(
    []
  );

  React.useEffect(() => {
    setGridData(deepCopy(claimLevelDataCopy));
    setPatientData([
      {
        name: 'MACK, GERIT A',
        DOB: '08/08/1996',
        gender: 'Male'
      }
    ]);
    setSubscriberData([
      {
        name: 'FORSS, LISA',
        DOB: '09/08/2000',
        gender: 'Female'
      }
    ]);
    setPayerData([
      {
        primaryInsurance: 'NGS Medicare',
        primaryPolicyId: '10112',
        secondaryInsurance: 'AARP',
        secondaryPolicyId: '109012',
        tertiaryInsurance: 'Health First',
        tertiaryPolicyId: '22548'
      }
    ]);
    setFacilityProviderData([
      {
        facilityLocation: props?.mrnData?.facilityLocation,
        physicalAddress: '14500 Avion Pkwy STE 125, Chantilly, 20151',
        department: '',
        placeOfService: 'OFFICE',
        renderingProvider: 'SMITH, JOHN D',
        assistantSurgeon: props?.mrnData?.assignedTo
      }
    ]);
  }, []);

  const mrnSectionData = {
    name: 'MRN',
    label: 'MRN',
    fields: [
      {data_column: 'MRN', label: 'MRN'},
      {data_column: 'claimId', label: '837 Claim ID'},
      {data_column: 'billingNpi', label: 'Billing NPI'},
      {data_column: 'billingTin', label: 'Billing TIN'}
    ]
  };

  const patientDetailsData = {
    name: 'patient_details',
    label: 'Patient Details',
    fields: [
      //   {data_column: 'MRN', label: 'MRN'},
      {data_column: 'name', label: 'Name'},
      {data_column: 'DOB', label: 'DOB'},
      {data_column: 'gender', label: 'Gender'}
      //   {data_column: 'relationship', label: 'Relationship'}
    ]
  };

  const subscriberDetailsData = {
    name: 'subscriber_details',
    label: 'Subscriber Details',
    fields: [
      {data_column: 'name', label: 'Name'},
      {data_column: 'DOB', label: 'DOB'},
      {data_column: 'gender', label: 'Gender'}
    ]
  };

  const payerDetailsData = {
    name: 'payer',
    label: 'Payer',
    fields: [
      {data_column: 'primaryInsurance', label: 'Primary Insurance'},
      {data_column: 'primaryPolicyId', label: 'Primary Policy ID'},
      {data_column: 'secondaryInsurance', label: 'Secondary Insurance'},
      {data_column: 'secondaryPolicyId', label: 'Secondary Policy ID'},
      {data_column: 'tertiaryInsurance', label: 'Tertiary Insurance'},
      {data_column: 'tertiaryPolicyId', label: 'Tertiary Policy ID'}
    ]
  };

  const facilityProviderDetails = {
    name: 'facilityOrProvider',
    label: 'Facility/Provider',
    fields: [
      {data_column: 'facilityLocation', label: 'Facility/Location'},
      {data_column: 'physicalAddress', label: 'Physical Address'},
      {data_column: 'department', label: 'Department'},
      {data_column: 'placeOfService', label: 'Place Of Service'},
      {data_column: 'renderingProvider', label: 'Rendering Provider'},
      {data_column: 'assistantSurgeon', label: 'Assistant Surgeon'}
    ]
  };

  const gridColumns = [
    {data_column: 'dos', label: 'DOS', skip_sorting: true, skip_filter: true},
    {
      data_column: 'revenue_code',
      label: 'Revenue Code',
      skip_sorting: true,
      skip_filter: true
    },
    {
      data_column: 'cptHCPCS',
      label: 'CPT/HCPCS',
      skip_sorting: true,
      skip_filter: true
    },
    {
      data_column: 'modifier',
      label: 'Modifier',
      skip_sorting: true,
      skip_filter: true
    },
    {
      data_column: 'units',
      label: 'Units',
      skip_sorting: true,
      skip_filter: true
    },
    {
      data_column: 'icd10',
      label: 'ICD 10',
      skip_sorting: true,
      skip_filter: true
    },
    {
      data_column: 'currentFinancialClass',
      label: 'Current Financial Class',
      skip_sorting: true,
      skip_filter: true
    },
    {
      data_column: 'insurance',
      label: 'Insurance',
      skip_sorting: true,
      skip_filter: true
    },
    {
      data_column: 'billed',
      label: 'Billed',
      skip_sorting: true,
      skip_filter: true
    },
    {
      data_column: 'outstanding',
      label: 'OutStanding',
      skip_sorting: true,
      skip_filter: true
    }
  ];

  return (
    <Box sx={{padding: '24px'}}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
        <ListSubheader
          disableGutters
          disableSticky
          onClick={props.onNavigateToMain}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            typography: 'overline',
            color: 'text.disabled',
            marginTop: '1rem',
            marginBottom: '20px',
            transition: theme1 =>
              theme1.transitions.create(['color'], {
                duration: theme1.transitions.duration.shortest
              }),
            '&:hover': {
              color: 'black'
            }
          }}>
          <IoChevronBackCircleOutline size={24} />
          <Box style={{marginLeft: 12, fontSize: 16}}>
            {`${props?.mrnData?.facilityLocation}`}
          </Box>
        </ListSubheader>
        <Box>
          <span style={{fontSize: '14px'}}>MRN</span>
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '16px !important'
            }}>{` ${props?.mrnData?.MRN}`}</span>
          <span style={{marginLeft: '6px', fontSize: '14px'}}>Claim ID</span>
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '16px !important'
            }}>{` ${props?.mrnData?.claimId837}`}</span>
        </Box>
      </Box>
      <Grid container rowSpacing={0.25} columnSpacing={{xs: 1, sm: 4, md: 4}}>
        <Grid item xs={12} md={6}>
          <CardComponent
            sectionUIData={patientDetailsData}
            data={[...patientData]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CardComponent
            sectionUIData={subscriberDetailsData}
            data={[...subscriberData]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CardComponent
            sectionUIData={payerDetailsData}
            data={[...payerData]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CardComponent
            sectionUIData={facilityProviderDetails}
            data={[...facilityProviderData]}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <Box sx={{...shadowBoxSx}}>
            <Typography
              sx={{
                fontWeight: 'bold',
                marginBottom: '12px',
                fontSize: '1rem !important'
              }}>
              Alerts
            </Typography>
            <Typography>
              <span style={{fontWeight: 'bold'}}>*</span> CPT 72141 and ICD
              M54.2 has 90% FPRR.
            </Typography>
            <Typography>
              <span style={{fontWeight: 'bold'}}>*</span> On an average, AARP
              pays Medicare crossover claims within 7 days.
            </Typography>
            {/* <Box sx={{justifyContent: 'flex-end', display: 'flex'}}>
              <LoadingButton
                type="submit"
                variant="contained"
                sx={{
                  marginLeft: '12px',
                  backgroundColor: theme.palette.primary.dark
                }}
                loadingPosition="start">
                Edit & Rebill
              </LoadingButton>
            </Box> */}
            <CollapsibleTable
              sx={{
                paddingLeft: '0px',
                paddingRight: '0px'
              }}
              disableToolbar
              displayHorizontalScroll
              loading={false}
              sectionUIData={{fields: gridColumns}}
              sectionRowsData={gridData}
              module={props?.module}
              sub_module={props?.sub_module || 'ar-worklist'}
              minHeight="75px"
            />
            <Typography
              sx={{
                fontWeight: 'bold',
                marginBottom: '12px',
                fontSize: '1rem !important'
              }}>
              Billing & Financial
            </Typography>
            {billingFinancialData.map((d, di) => {
              return (
                <Typography
                  key={`claim-billing_${di}`}
                  sx={{marginBottom: '4px'}}>
                  <span style={{fontWeight: 'bold'}}>*</span>
                  <span>{` ${d.action}`}</span>
                  <span
                    style={{
                      color: theme.palette.primary.dark,
                      fontWeight: 'bold'
                    }}>
                    {d.datetime ? <span>{` - ${d.datetime}`}</span> : null}
                  </span>
                  <span style={{fontWeight: 'bold', color: '#964B00'}}>
                    {d.username ? <span>{` - ${d.username}`}</span> : null}
                  </span>
                </Typography>
              );
            })}
          </Box>
        </Grid>
        <Grid item xs={12} md={12}>
          <Box sx={{...shadowBoxSx}}>
            <Typography
              sx={{
                fontWeight: 'bold',
                marginBottom: '12px',
                fontSize: '1rem !important'
              }}>
              Comments
            </Typography>
            <Typography sx={{fontWeight: 'bold', color: 'error.main'}}>
              Pre-service Clearance
            </Typography>
            <Typography sx={{marginTop: '4px'}}>
              <span
                style={{
                  fontWeight: 'bold',
                  color: theme.palette.primary.dark
                }}>
                {`09/15/24 04:00 PM - `}
              </span>
              <span style={{fontWeight: 'bold', color: '#964B00'}}>
                {`Charles A`}
              </span>
              <span>{` : `}</span>
              <span>{`Medicare is primary || MCO : N/A || Part B Ded Rem : $0.00 || Tran ID : 67532PI96`}</span>
            </Typography>
            <Typography
              sx={{fontWeight: 'bold', color: 'error.main', marginTop: '20px'}}>
              Billing & Coding
            </Typography>
            <Typography sx={{marginTop: '4px'}}>
              <span
                style={{
                  fontWeight: 'bold',
                  color: theme.palette.primary.dark
                }}>
                {`10/03/24 04:00 PM - `}
              </span>
              <span style={{fontWeight: 'bold', color: '#964B00'}}>
                {`Jenni Adams`}
              </span>
              <span>{` : `}</span>
              <span>{`Corrected the MBI and billed.`}</span>
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClaimLevelView;
